import { useState } from 'react';
import type { DragEvent as ReactDragEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SearchX, Upload } from 'lucide-react';
import { RootState } from '../../../../store/store';
import { useAssetCart } from '@/components/ui/carousel';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { cn } from '@/lib/utils';
import AssetsImageCard from './AssetsImageCard';
import {
  reorderAssets,
  restoreImageAssets,
  setShowHidden
} from '../../slice/ImageAssetSlice';
import { reorderByPhotoUUID } from '../../edit/reorderList';
import {
  canStagePhoto,
  isInPool,
  isPhotoInView
} from '../../model/assetViewScope';
import { buildOrderedAssetUUIDs } from '../../edit/buildReorderPayload';
import { persistPhotosetOrder } from '../../edit/assetEditSeams';
import {
  canUseEditSuite,
  isInternalAssetTeamUser
} from '../../assetFeatureFlags';
import FloorPlanEmptyState from '../../floor-plan/FloorPlanEmptyState';
import { showAlert } from '@/slice/AlertSlice';
import { Variants } from '@/types/common';

type AssetsImageGridProps = {
  onImageClick: (index: number) => void;
  /** Withheld by the page when the user may not upload here (DV-2820). */
  onOpenUpload?: () => void;
  onStage?: (photoUUID: string) => void;
  onDownloadPhoto?: (photoUUID: string) => void;
  onDelete?: (photoUUID: string) => void;
  /**
   * The page's drop overlay is up over this grid. Its text is centered right
   * where an empty grid's placeholder sits, so the placeholder steps aside
   * (invisible, keeping its space) until the drag ends. Photos stay visible:
   * the overlay is translucent so they show through.
   */
  dropOverlayActive?: boolean;
  // DV-2221: the selected photoset's full backend asset order + its photo
  // subset — the permutation base for reorder persistence (Zillow url assets
  // must keep their positions in the PUT payload).
  reorderContext?: {
    fullAssetOrder: string[];
    photoAssetUUIDs: ReadonlySet<string>;
  };
};

const AssetsImageGrid = ({
  onImageClick,
  onOpenUpload,
  onStage,
  onDownloadPhoto,
  onDelete,
  reorderContext,
  dropOverlayActive = false
}: AssetsImageGridProps) => {
  const dispatch = useDispatch();
  const imageAssets = useSelector(
    (state: RootState) => state.imageAsset.imageAssets
  );
  const showHidden = useSelector(
    (state: RootState) => state.imageAsset.showHidden
  );
  const searchQuery = useSelector(
    (state: RootState) => state.imageAsset.searchQuery
  );
  const hasQuery = searchQuery.trim() !== '';
  const { cartAssetIdSet } = useAssetCart(true);
  const editEnabled = canUseEditSuite();
  // The tab's photos (DV-3735): every photo, or the floor plans only. The
  // floor-plan grid does not reorder: the order is the whole photoset's, and a
  // drop between two floor plans says nothing about where the photo goes among
  // the photos between them. Reordering stays on Photos.
  const pool = useSelector((state: RootState) => state.imageAsset.pool);
  const reorderEnabled = editEnabled && pool === 'all';
  const poolAssets = imageAssets.filter((image) => isInPool(image, pool));
  // Every empty-grid placeholder below wears it (see dropOverlayActive).
  const placeholderClass = dropOverlayActive ? 'invisible' : undefined;
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<{
    photoUUID: string;
    position: 'before' | 'after';
  } | null>(null);

  const clearDrag = () => {
    setDraggingId(null);
    setDropTarget(null);
  };

  const handleDrop = (
    toPhotoUUID: string,
    e: ReactDragEvent<HTMLDivElement>
  ) => {
    e.preventDefault(); // DnD hygiene: some browsers navigate on drop otherwise
    if (!draggingId || !dropTarget || dropTarget.photoUUID !== toPhotoUUID) {
      clearDrag();
      return;
    }
    const { position } = dropTarget;
    const fromPhotoUUID = draggingId;
    dispatch(reorderAssets({ fromPhotoUUID, toPhotoUUID, position }));
    // Persist the FULL resulting order (decided contract: PUT orderedAssetUUIDs).
    // Recompute with the same pure helper the reducer uses, from the same
    // pre-dispatch list, so the payload can't diverge from the store.
    const next = reorderByPhotoUUID(
      imageAssets,
      fromPhotoUUID,
      toPhotoUUID,
      position
    );
    // First DEFINED photosetUUID, not next[0] — position 0 can legitimately
    // lack one (e.g. a just-uploaded asset) while the rest of the list is
    // tied to a real photoset, and that must not skip persistence.
    const photosetId = next.find((i) => i.photosetUUID)?.photosetUUID;
    if (next !== imageAssets && photosetId) {
      // Staged photos are committed Assets (DV-2252) — real ids, so they
      // participate in the persisted order like any other photo.
      const gridOrder = next.map((i) => i.assetUUID);
      const orderedAssetUUIDs = reorderContext
        ? buildOrderedAssetUUIDs(
            reorderContext.fullAssetOrder,
            reorderContext.photoAssetUUIDs,
            gridOrder
          )
        : gridOrder;
      const preDragAssets = imageAssets;
      persistPhotosetOrder({ photosetId, orderedAssetUUIDs }).catch(() => {
        // Backend rejected (permutation mismatch, network, 4xx/5xx): restore
        // the exact pre-drag order so the UI never silently diverges.
        dispatch(restoreImageAssets(preDragAssets));
        dispatch(
          showAlert({
            type: Variants.ERROR,
            message:
              'Could not save the new photo order — it has been reset. Please try again.'
          })
        );
      });
    }
    clearDrag();
  };

  const dragProps = reorderEnabled
    ? {
        onDragStart: (photoUUID: string, e: ReactDragEvent<HTMLDivElement>) => {
          // Firefox never initiates a drag unless dataTransfer.setData is
          // called in the dragstart handler. Drag identity still comes from
          // component state (draggingId) — never from getData on drop.
          e.dataTransfer?.setData('text/plain', photoUUID);
          if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
          setDraggingId(photoUUID);
        },
        onDragOver: (photoUUID: string, e: ReactDragEvent<HTMLDivElement>) => {
          e.preventDefault(); // required for drop to fire
          if (!draggingId || photoUUID === draggingId) return;
          const rect = e.currentTarget.getBoundingClientRect();
          const position =
            e.clientX < rect.left + rect.width / 2 ? 'before' : 'after';
          setDropTarget((prev) =>
            prev?.photoUUID === photoUUID && prev.position === position
              ? prev
              : { photoUUID, position }
          );
        },
        onDrop: handleDrop,
        onDragEnd: clearDrag
      }
    : undefined;

  if (poolAssets.length === 0) {
    if (pool === 'floorPlans') {
      return (
        <FloorPlanEmptyState
          onUpload={editEnabled ? onOpenUpload : undefined}
          className={placeholderClass}
        />
      );
    }
    // No upload affordance — an empty photoset renders nothing, matching the
    // pre-edit-suite prod page. onOpenUpload is withheld by the page when the
    // user may not upload here (DV-2820), which is exactly this state for a
    // customer: no photoset means an upload would create an asset the trash
    // endpoints cannot scope, i.e. one the UI can never delete.
    if (!editEnabled || !onOpenUpload) return null;
    // Same grid as the populated view, so the dropzone takes one card slot
    // instead of stretching across the page.
    return (
      <div
        className={cn(
          'grid grid-cols-2 gap-3 sm:grid-cols-[repeat(auto-fill,minmax(266px,1fr))]',
          placeholderClass
        )}
      >
        <button
          type="button"
          onClick={onOpenUpload}
          className="flex aspect-[3/2] cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border px-4 text-center hover:border-primary"
        >
          <Upload className="h-7 w-7 text-muted-foreground" />
          <span className="text-base font-medium">Upload Photos</span>
          <span className="text-sm text-muted-foreground">
            Drag photos here or click to browse — JPG or PNG
          </span>
        </button>
      </div>
    );
  }

  // A hidden STAGED photo is internal WIP (an unapproved AI render parked
  // out of sight — see StagedAssetsGrid), not a customer's own hide, so for
  // customer roles it is not part of the hidden pool at all: Show Hidden
  // must not reveal it, the hint must not count it, and keeping it out of
  // the grid is also what keeps the unhide control off it. Role-aware
  // interpretation of an overloaded flag — goes away when staged assets get
  // a real draft/approved lifecycle state in asset-service.
  const seesHiddenStaged = isInternalAssetTeamUser();
  const hiddenPool = poolAssets.filter(
    (image) => image.hidden && (seesHiddenStaged || !image.staged)
  );
  const hiddenCount = hiddenPool.length;
  const hiddenPoolUUIDs = new Set(hiddenPool.map((image) => image.photoUUID));
  // Keep each card's ORIGINAL index: the viewer modal receives the
  // unfiltered imageAssets list, so a filtered-out photo must not shift
  // the indexes of the ones still shown.
  const visibleEntries = imageAssets
    .map((image, index) => ({ image, index }))
    .filter(
      ({ image }) =>
        // The shared view-filter rule (model/assetViewScope — the same one the
        // select-all reducer and every count read), plus this grid's role rule
        // on top: a hidden STAGED photo is outside a customer's hidden pool
        // entirely, so Show Hidden must not reveal it.
        isPhotoInView(image, { showHidden, searchQuery, pool }) &&
        (!image.hidden || hiddenPoolUUIDs.has(image.photoUUID))
    );

  const footHint =
    editEnabled &&
    (hiddenCount > 0 ? (
      <p className="mt-3 text-sm text-muted-foreground">
        {hiddenCount} {hiddenCount === 1 ? 'photo' : 'photos'} hidden — kept
        here, excluded from Sync and PlanOtour.{' '}
        {!showHidden && (
          <Button
            variant="link"
            type="button"
            // `h-auto p-0` strips the primitive's tap floor, so this inline
            // link carries it at the call site — the convention button.tsx
            // records for standalone links (Unit 3 precedent).
            className="h-auto p-0 text-sm max-lg:min-h-11"
            onClick={() => dispatch(setShowHidden(true))}
          >
            Show Hidden
          </Button>
        )}
      </p>
    ) : (
      <p className="mt-3 text-sm text-muted-foreground">
        {reorderEnabled
          ? 'Drag any photo to reorder · click a caption to rename · hover a photo to see more options.'
          : 'Click a caption to rename · hover a photo to see more options.'}
      </p>
    ));

  // DV-2856 no-match state: the shared EmptyState hero, same icon and copy the
  // Orders and Properties lists use (decided 2026-09-18, Q3b — consistency won
  // over a richer form). Checked BEFORE the all-hidden case below: with a query
  // active, "nothing matched" is the accurate explanation, not "everything is
  // hidden".
  if (visibleEntries.length === 0 && hasQuery) {
    return (
      <EmptyState
        icon={SearchX}
        title="No Matches"
        description="Try adjusting your filters or search criteria"
        className={placeholderClass}
      />
    );
  }

  // Every photo on the property is hidden: this is not an empty photoset,
  // so the upload dropzone stays off and only the foot-hint renders.
  if (visibleEntries.length === 0) {
    return <>{footHint}</>;
  }

  return (
    <>
      {/* Base grid-cols-2, not auto-fill: below sm one 266px column is all
          that fits, and the design's phone grid is two-up (2026-08-19
          round 4). At sm+ auto-fill resumes and packs as many 266px-min
          columns as the width allows. */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-[repeat(auto-fill,minmax(266px,1fr))]">
        {visibleEntries.map(({ image, index }, viewIndex) => (
          <AssetsImageCard
            key={image.photoUUID}
            photoUUID={image.photoUUID}
            index={index}
            inCart={cartAssetIdSet.has(image.assetUUID)}
            onImageClick={onImageClick}
            nextPhotoUUID={visibleEntries[viewIndex + 1]?.image.photoUUID}
            prevPhotoUUID={visibleEntries[viewIndex - 1]?.image.photoUUID}
            dragProps={dragProps}
            dropIndicator={
              dropTarget?.photoUUID === image.photoUUID
                ? dropTarget.position
                : null
            }
            onStage={canStagePhoto(image) ? onStage : undefined}
            onDownload={onDownloadPhoto}
            onDelete={onDelete}
          />
        ))}
        {/* DV-2856: no upload tile while filtering — a create affordance in a
            find view is noise, and it would sit alone beside two matches. */}
        {editEnabled && onOpenUpload && !hasQuery && (
          <button
            type="button"
            onClick={onOpenUpload}
            className="flex aspect-[3/2] cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-border text-muted-foreground hover:border-primary hover:text-primary"
          >
            <Upload className="h-5 w-5" />
            <span className="text-sm font-medium">Upload Images</span>
          </button>
        )}
      </div>
      {footHint}
    </>
  );
};

export default AssetsImageGrid;
